import { useEffect, useMemo } from 'react';
import axios, { AxiosError, AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import { useRefreshApi } from './use-refresh-api';

interface UseAuthenticatedApiOptions {
    accessToken: string;
    refreshToken: string;
    refreshUrl: string;
    baseUrl: string;
    axiosInstance?: AxiosInstance;
}

interface UseAuthenticatedApiResponse {
    api: AxiosInstance;
}

let requestInterceptor: number | null = null;
let responseInterceptor: number | null = null;

function useAuthenticatedApi({
    accessToken,
    refreshToken,
    refreshUrl,
    baseUrl,
    axiosInstance
}: UseAuthenticatedApiOptions): UseAuthenticatedApiResponse {
    const api = useMemo(() => {
        const api = axiosInstance ? axiosInstance : axios.create();
        api.defaults.timeout = 30000; // 30 seconds
        axiosRetry(api, {
            retries: 0,
            shouldResetTimeout: true,
            // eslint-disable-next-line @typescript-eslint/unbound-method
            retryDelay: axiosRetry.exponentialDelay,
            retryCondition: (error) => {
                // if retry condition is not specified, by default idempotent requests are retried
                if (!error.response) {
                    return true;
                }
                return error.response.status >= 500;
            }
        });

        return api;
    }, [axiosInstance]);

    const { refreshTokenApi } = useRefreshApi({
        baseUrl,
        refreshToken,
        refreshUrl
    });

    useEffect(() => {
        void refreshTokenApi();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (requestInterceptor) {
            api.interceptors.request.eject(requestInterceptor);
        }
        if (responseInterceptor) {
            api.interceptors.response.eject(responseInterceptor);
        }

        requestInterceptor = api.interceptors.request.use((config) => {
            config.baseURL = baseUrl;

            config.headers.Authorization = `Bearer ${accessToken}`;

            return config;
        });

        responseInterceptor = api.interceptors.response.use(
            (response) => {
                return response;
            },
            (error: AxiosError) => {
                const originalRequest = error.config;
                if (error.response?.status === 401) {
                    if (!originalRequest) {
                        return Promise.reject(error);
                    }
                    return refreshTokenApi().then((newAccessToken) => {
                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                        return axios(originalRequest);
                    });
                }

                return Promise.reject(error);
            }
        );
    }, [api, refreshTokenApi, baseUrl, accessToken]);

    return {
        api
    };
}

export { useAuthenticatedApi };
